package com.tovtalk.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectHorizontalDragGestures
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.draw.blur
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import com.tovtalk.data.analytics.AnalyticsClient
import com.tovtalk.data.analytics.ExitReason
import com.tovtalk.data.analytics.StartedFrom
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.Post
import com.tovtalk.ui.LocalAppInForeground
import com.tovtalk.ui.adaptive.LocalWindowSizeClass
import com.tovtalk.ui.adaptive.WindowSizeClass
import com.tovtalk.ui.platform.WebReelCell
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.config.isWeb
import com.tovtalk.utils.format.formatCount
import com.tovtalk.utils.logger.Logger
import com.tovtalk.utils.strings.StringProvider
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@Composable
fun ReelItem(
    post: Post,
    isVisible: Boolean,
    /**
     * True for cells that are NOT currently visible but are immediate neighbors
     * (N-1 / N+1). When true, we mount the player + AVPlayer/ExoPlayer in a
     * paused state so the first frame is buffered and rendered. When user
     * scrolls onto this cell, transition is video → video instead of
     * video → thumbnail → video.
     */
    isPrerendering: Boolean = false,
    nextVideoUrl: String? = null,
    isPausedByComments: Boolean = false,
    currentUserId: Int? = null,
    videoMuted: Boolean = true,
    showMuteButton: Boolean = false,
    onToggleMute: (() -> Unit)? = null,
    isGuestMode: Boolean = false,
    onGuestActionRequired: (() -> Unit)? = null,
    onShowGuestPromptSheet: (() -> Unit)? = null,
    onLikeClick: () -> Unit,
    onCommentClick: () -> Unit,
    onShareClick: () -> Unit,
    onReportClick: () -> Unit = {},
    onSaveClick: () -> Unit = {},
    onUserClick: () -> Unit,
    onFollowClick: () -> Unit = {},
    onVideoEnded: () -> Unit = {},
    onHashtagClick: (String) -> Unit = {},
    commentsEnabled: Boolean = true,
    showReactionIcon: Boolean = true,
    startedFrom: StartedFrom = StartedFrom.FEED,
    onNavigateToStories: ((Int) -> Unit)? = null,
) {
    // Hide follow button when guest, or when current user already follows this post's user
    val showFollowButton = !isGuestMode && currentUserId != null &&
            post.author.id != currentUserId &&
            !post.viewerState.isFollowing
    var isPlaying by remember { mutableStateOf(true) }
    var showDoubleTapHeart by remember { mutableStateOf(false) }
    var playbackRate by remember { mutableFloatStateOf(1f) }
    var isMuted by remember { mutableStateOf(videoMuted) }
    var guestOptimisticLiked by remember { mutableStateOf(false) }
    var isDescriptionExpanded by remember { mutableStateOf(false) }
    var videoReady by remember { mutableStateOf(false) }
    // PERF(3a): track the latest reported playback position so the comments
    // pause/resume analytics can report an accurate position.
    var lastPositionMs by remember { mutableStateOf(0L) }
    val scope = rememberCoroutineScope()
    // PERF(4): Single remembered Job for the double-tap heart auto-hide. Previously every
    // tap handler did its own scope.launch { delay(800); ... }, so rapid double-taps stacked
    // independent coroutines that fought over showDoubleTapHeart. Now each trigger cancels the
    // previous job before scheduling the hide, so only one timer is ever in flight.
    var heartHideJob by remember { mutableStateOf<kotlinx.coroutines.Job?>(null) }
    val triggerDoubleTapHeart: () -> Unit = {
        showDoubleTapHeart = true
        heartHideJob?.cancel()
        heartHideJob = scope.launch {
            delay(800)
            showDoubleTapHeart = false
        }
    }
    val windowSizeClass = LocalWindowSizeClass.current
    val isWideScreen = windowSizeClass >= WindowSizeClass.Medium
    val displayLiked = if (isGuestMode) guestOptimisticLiked else post.viewerState.isLiked
    val inForeground = LocalAppInForeground.current
    val analytics: AnalyticsClient = koinInject()

    // C8: video session lifecycle analytics; C9: current-post tracking for screenshot detection
    DisposableEffect(post.id, isVisible) {
        if (isVisible) {
            analytics.setCurrentPost(post.id.toLong())
            scope.launch {
                analytics.onVideoStarted(
                    postId = post.id.toLong(),
                    videoDurationMs = (post.duration ?: 0) * 1000L,
                    startedFrom = startedFrom,
                )
            }
        }
        onDispose {
            if (isVisible) {
                analytics.setCurrentPost(null)
                scope.launch {
                    // PERF(3b): the real exit reason (swipe direction / profile click /
                    // navigation away) is NOT available at this call site — disposal of the
                    // cell happens for all of those cases identically and no reason is threaded
                    // into ReelItem. Leaving SWIPE_NEXT (the dominant case in a vertical feed)
                    // to preserve existing behavior; threading the true reason would require a
                    // ViewModel/feed-level change which is out of scope for this UI-only pass.
                    analytics.onVideoEnded(
                        postId = post.id.toLong(),
                        exitReason = ExitReason.SWIPE_NEXT,
                    )
                }
            }
        }
    }

    val handleLikeClick: () -> Unit = {
        if (isGuestMode) {
            guestOptimisticLiked = true
            scope.launch {
                delay(500)
                onShowGuestPromptSheet?.invoke()
            }
        } else {
            onLikeClick()
        }
    }

    LaunchedEffect(isVisible, inForeground) {
        isPlaying = isVisible && inForeground
    }
    LaunchedEffect(videoMuted) {
        isMuted = videoMuted
    }

    val effectivePlaying = isPlaying && !isPausedByComments && !isDescriptionExpanded

    // PERF(3a): the watch session was never paused in analytics when the comments
    // sheet opened, even though playback stops (effectivePlaying becomes false).
    // Emit an explicit pause when comments open and resume when they close, for the
    // visible cell only. skipInitial guards against emitting a spurious resume on
    // first composition (when comments are already closed).
    var commentsPauseInitialized by remember { mutableStateOf(false) }
    LaunchedEffect(isPausedByComments, isVisible) {
        if (!isVisible) {
            commentsPauseInitialized = true
            return@LaunchedEffect
        }
        if (!commentsPauseInitialized) {
            commentsPauseInitialized = true
            // Don't emit on the initial state; only react to subsequent toggles.
            return@LaunchedEffect
        }
        if (isPausedByComments) {
            analytics.onVideoPaused(post.id.toLong(), lastPositionMs)
        } else {
            analytics.onVideoResumed(post.id.toLong(), lastPositionMs)
        }
    }

    val videoUrl = post.videoUrl

    LaunchedEffect(isVisible, videoUrl) {
        if (isVisible && videoUrl.isNotBlank()) {
            Logger.d("TT_VIDEO: entered_video_view | source=feed | postId=${post.id}", "TT_VIDEO")
        }
        if (!isVisible) videoReady = false
    }

    val dragOnlyModifier = Modifier.pointerInput(isWideScreen, isWeb()) {
        detectTapGestures(
            onPress = {
                try {
                    awaitRelease()
                } finally {
                    playbackRate = 1f
                }
            },
            onLongPress = { playbackRate = 2f },
            // On wide-screen web, the DOM overlay is pointer-events:none so Compose must
            // handle tap (play/pause) and double-tap (like) itself. Mobile/native keep
            // their DOM overlay / ReelOverlay handling, so no tap handler is attached there.
            onTap = if (isWeb() && isWideScreen) { _ -> isPlaying = !isPlaying } else null,
            onDoubleTap = if (isWeb() && isWideScreen) { _ ->
                if (!displayLiked) handleLikeClick()
                triggerDoubleTapHeart() // PERF(4): single-job heart animation
            } else null,
        )
    }
    Box(
        modifier = Modifier
            .fillMaxSize()
            .then(dragOnlyModifier)
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(TovTalkTheme.colors.videoBackground)
                .align(Alignment.Center)
        )
        val thumbnail = post.thumbnail
        if (!videoReady && !thumbnail.isNullOrBlank()) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .blur(TovTalkTheme.sizes.blurRadius)
                    .alpha(TovTalkTheme.opacity.nearOpaque)
                    .align(Alignment.Center)
            ) {
                TovTalkAsyncImage(
                    url = thumbnail,
                    modifier = Modifier.fillMaxSize()
                )
            }
        } else if (!videoReady) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(
                        brush = Brush.verticalGradient(
                            colors = listOf(
                                TovTalkTheme.colors.videoOverlayGradientTop,
                                TovTalkTheme.colors.videoBackground,
                                TovTalkTheme.colors.videoOverlayGradientBottom
                            )
                        )
                    )
                    .align(Alignment.Center)
            )
        }
        Box(modifier = Modifier.fillMaxSize()) {
            if (isWeb()) {
                WebReelCell(
                    modifier = Modifier.fillMaxSize(),
                    videoUrl = videoUrl,
                    isVisible = isVisible,
                    isPlaying = effectivePlaying,
                    videoMuted = videoMuted,
                    isLiked = displayLiked,
                    likeCount = formatCount(post.stats.likes),
                    commentCount = formatCount(post.stats.comments),
                    shareCount = formatCount(post.stats.shares),
                    username = post.author.username,
                    avatarUrl = post.author.profilePhoto,
                    description = post.caption,
                    musicTitle = post.music?.title ?: post.music?.let { StringProvider.get("video_original_sound") },
                    viewCountText = "${formatCount(post.stats.views)} ${StringProvider.get("video_views")}",
                    showFollowButton = showFollowButton,
                    followButtonText = StringProvider.get("profile_follow"),
                    showDoubleTapHeart = showDoubleTapHeart,
                    onLikeClick = handleLikeClick,
                    onCommentClick = onCommentClick,
                    onShareClick = onShareClick,
                    onUserClick = onUserClick,
                    onFollowClick = onFollowClick,
                    onTap = { isPlaying = !isPlaying },
                    onDoubleTap = {
                        if (!displayLiked) handleLikeClick()
                        triggerDoubleTapHeart() // PERF(4): single-job heart animation
                    },
                    onVideoEnded = onVideoEnded
                )
            } else {
                if (videoUrl.isNotBlank() && (isVisible || isPrerendering)) {
                    TovTalkReelPlayer(
                        url = videoUrl,
                        // Adjacent prerendered cells mount the player + buffer
                        // the first frame, but do not actually play (no audio,
                        // no progress) until they become visible.
                        isPlaying = effectivePlaying && isVisible,
                        thumbnailUrl = thumbnail,
                        modifier = Modifier.fillMaxSize(),
                        muted = isMuted || !isVisible,
                        playbackRate = playbackRate,
                        onVideoEnded = onVideoEnded,
                        fillContainer = true,
                        nextVideoUrl = nextVideoUrl,
                        onVideoReady = { videoReady = true },
                        allSources = post.allSources,
                        onPositionUpdate = { posMs, durMs ->
                            // PERF(3a): remember the latest position so comments pause/resume
                            // can report it accurately.
                            lastPositionMs = posMs
                            // Only the visible cell reports progress; prerendered
                            // cells stay silent in analytics.
                            if (isVisible) {
                                scope.launch {
                                    analytics.onVideoProgress(
                                        postId = post.id.toLong(),
                                        positionMs = posMs,
                                        isPlaying = effectivePlaying && isVisible,
                                        soundOn = !isMuted,
                                    )
                                }
                            }
                        },
                        onVideoLooped = {
                            if (isVisible) {
                                scope.launch { analytics.onVideoLooped(post.id.toLong()) }
                            }
                        },
                    )
                } else if (!thumbnail.isNullOrBlank()) {
                    // Far-away page (|distance| ≥ 2): show full-screen thumbnail.
                    TovTalkAsyncImage(
                        url = thumbnail,
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop
                    )
                }
                // Tap layer stays under overlay controls so action buttons remain clickable.
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .pointerInput(Unit) {
                            detectTapGestures(
                                // Press-and-hold to play at 2x; release restores 1x. Mirrors
                                // the web behavior in dragOnlyModifier — here on the tap layer
                                // because it's what receives gestures on Android/iOS.
                                onPress = {
                                    try {
                                        awaitRelease()
                                    } finally {
                                        playbackRate = 1f
                                    }
                                },
                                onLongPress = { playbackRate = 2f },
                                onTap = {
                                    if (!isPausedByComments && !isDescriptionExpanded) {
                                        isPlaying = !isPlaying
                                    }
                                },
                                onDoubleTap = {
                                    if (isDescriptionExpanded) return@detectTapGestures
                                    if (!displayLiked) handleLikeClick()
                                    triggerDoubleTapHeart() // PERF(4): single-job heart animation
                                }
                            )
                        }
                )
                ReelOverlay(
                    modifier = Modifier.fillMaxSize(),
                    topGradientFraction = 0.15f,
                    bottomGradientFraction = 0.35f,
                    isPlaying = effectivePlaying,
                    showDoubleTapHeart = showDoubleTapHeart,
                    avatarUrl = post.author.profilePhoto,
                    username = post.author.username,
                    displayName = post.author.fullname,
                    showFollowButton = showFollowButton,
                    onUserClick = onUserClick,
                    onFollowClick = onFollowClick,
                    followButton = { ReelFollowPlusButton(onClick = onFollowClick) },
                    description = post.caption,
                    descriptionExpanded = isDescriptionExpanded,
                    onDescriptionExpand = { isDescriptionExpanded = true },
                    onDescriptionCollapse = { isDescriptionExpanded = false },
                    musicTitle = post.music?.title ?: post.music?.let { StringProvider.get("video_original_sound") },
                    viewCountText = "${formatCount(post.stats.views)} ${StringProvider.get("video_views")}",
                    isLiked = displayLiked,
                    likeCount = formatCount(post.stats.likes),
                    commentCount = formatCount(post.stats.comments),
                    shareCount = formatCount(post.stats.shares),
                    onLikeClick = handleLikeClick,
                    onCommentClick = onCommentClick,
                    onShareClick = onShareClick,
                    onReportClick = onReportClick,
                    isSaved = post.viewerState.isSaved,
                    saveCount = formatCount(post.stats.saves),
                    onSaveClick = onSaveClick,
                    showMuteButton = showMuteButton,
                    isMuted = isMuted,
                    onMuteClick = {
                        if (onToggleMute != null) onToggleMute() else isMuted = !isMuted
                    },
                    onHashtagClick = onHashtagClick,
                    commentsEnabled = commentsEnabled,
                    showReactionIcon = showReactionIcon,
                    hideActions = isWideScreen,
                    createdAt = post.createdAt,
                    hashtags = post.hashtags,
                    hasAuthorStory = post.author.hasActiveStory,
                    onAuthorStoryClick = onNavigateToStories?.let { nav -> { nav(post.author.id) } },
                )
            }
        }
        // "2x" speed badge shown while the user presses-and-holds the video.
        AnimatedVisibility(
            visible = playbackRate > 1f,
            enter = fadeIn(),
            exit = fadeOut(),
            modifier = Modifier
                .align(Alignment.TopCenter)
                .padding(top = 24.dp),
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                modifier = Modifier
                    .clip(RoundedCornerShape(50))
                    .background(Color(0x99000000))
                    .padding(horizontal = 12.dp, vertical = 6.dp),
            ) {
                Icon(
                    imageVector = Icons.Filled.PlayArrow,
                    contentDescription = null,
                    tint = Color.White,
                    modifier = Modifier.size(16.dp),
                )
                Text(
                    text = "2x",
                    style = TovTalkTheme.typography.titleSmall,
                    color = Color.White,
                )
            }
        }
    }
}
